import { useQuery } from '@tanstack/react-query';
import { roTeamAPI } from '../services/api';

/**
 * React Query hooks for the RO Team Dashboard.
 *
 * All hooks:
 * - Require the user to be a Reporting Officer (backend enforces this — a 403
 *   response is returned as an `error` state, not thrown to an error boundary).
 * - Use a 3-minute stale time (team data doesn't change by the second).
 * - Auto-refresh every 5 minutes for the overview (near real-time feel).
 * - Longer stale / no auto-refresh for trends, insights, and individual detail.
 */

const FIVE_MINUTES = 5 * 60 * 1000;
const THREE_MINUTES = 3 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;

/** Suppress retry on 403 / 404 — these are authoritative responses. */
const retryFn = (failureCount, error) => {
  const s = error?.response?.status;
  if (s === 403 || s === 404) return false;
  return failureCount < 2;
};

// ---------------------------------------------------------------------------
// Overview — today's team snapshot
// ---------------------------------------------------------------------------

/**
 * @param {string} [date] - Optional YYYY-MM-DD to query a specific past date.
 */
export const useROTeamOverview = (date) => {
  return useQuery({
    queryKey: ['ro-team', 'overview', date ?? 'today'],
    queryFn: async () => {
      const response = await roTeamAPI.getOverview(date);
      return response.data;
    },
    staleTime: THREE_MINUTES,
    refetchInterval: FIVE_MINUTES,
    retry: retryFn,
  });
};

// ---------------------------------------------------------------------------
// Trends — weekly aggregated patterns
// ---------------------------------------------------------------------------

/**
 * @param {number} [weeks=8] - How many complete past weeks to fetch.
 */
export const useROTeamTrends = (weeks = 8) => {
  return useQuery({
    queryKey: ['ro-team', 'trends', weeks],
    queryFn: async () => {
      const response = await roTeamAPI.getTrends(weeks);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
  });
};

// ---------------------------------------------------------------------------
// Leave insights — year-level leave analysis
// ---------------------------------------------------------------------------

/**
 * @param {number} [year] - 4-digit year. Defaults to current year.
 */
export const useROTeamLeaveInsights = (year) => {
  return useQuery({
    queryKey: ['ro-team', 'leave-insights', year ?? 'current'],
    queryFn: async () => {
      const response = await roTeamAPI.getLeaveInsights(year);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
  });
};

// ---------------------------------------------------------------------------
// Individual detail — per-employee monthly view
// ---------------------------------------------------------------------------

/**
 * @param {string|number|null} staffId - PK of the direct report. Hook is
 *   disabled when staffId is falsy.
 * @param {string} [month] - Optional YYYY-MM. Defaults to current month.
 */
export const useROTeamIndividual = (staffId, month) => {
  return useQuery({
    queryKey: ['ro-team', 'individual', staffId, month ?? 'current'],
    queryFn: async () => {
      const response = await roTeamAPI.getIndividualDetail(staffId, month);
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
    enabled: Boolean(staffId),
  });
};

// ---------------------------------------------------------------------------
// Upcoming leave — next N days
// ---------------------------------------------------------------------------

/**
 * @param {number} [days=7] - Lookahead window (1–30).
 */
export const useROTeamUpcomingLeave = (days = 7) => {
  return useQuery({
    queryKey: ['ro-team', 'upcoming-leave', days],
    queryFn: async () => {
      const response = await roTeamAPI.getUpcomingLeave(days);
      return response.data;
    },
    staleTime: TEN_MINUTES,
    retry: retryFn,
  });
};
